import React from 'react';
import { InputText } from 'primereact/inputtext';
import { LocateFixed, RefreshCw, Wind } from 'lucide-react';
import { distanceUnitLabel, speedUnitLabel, toDisplayDistance, toDisplaySpeed } from '../constants/units';
import { CourseDetailStatus, LocationStatus } from '../hooks/useRoundConditions';
import { CourseSearchItem, MeasurementSystem, WindConditions } from '../types';

interface RoundConditionsPanelProps {
  course: CourseSearchItem;
  courseDetailStatus: CourseDetailStatus;
  currentHole: number;
  distanceYards: number | null;
  holeCount: number;
  isRefreshing: boolean;
  locationStatus: LocationStatus;
  manualDistance: string;
  measurementSystem: MeasurementSystem;
  onManualDistanceChange: (value: string) => void;
  onHoleChange: (hole: number) => void;
  onRefresh: () => void;
  wind: WindConditions | null;
}

const locationMessage = (status: LocationStatus, hole: number): string => {
  if (status === 'denied') return 'Enable location permission to measure distance.';
  if (status === 'unsupported') return 'GPS is not available in this browser.';
  if (status === 'unavailable') return 'Location is unavailable. Tap refresh to try again.';
  return `Locating hole ${hole} green...`;
};

const RoundConditionsPanel: React.FC<RoundConditionsPanelProps> = ({
  course,
  courseDetailStatus,
  currentHole,
  distanceYards,
  holeCount,
  isRefreshing,
  locationStatus,
  manualDistance,
  measurementSystem,
  onManualDistanceChange,
  onHoleChange,
  onRefresh,
  wind,
}) => {
  const displayDistance = distanceYards == null
    ? null
    : Math.round(toDisplayDistance(distanceYards, measurementSystem));
  const displayWind = wind == null
    ? null
    : Math.round(toDisplaySpeed(wind.speedMph, measurementSystem));
  const gpsFailed = locationStatus === 'denied'
    || locationStatus === 'unsupported'
    || locationStatus === 'unavailable';
  const greenUnavailable = courseDetailStatus === 'unavailable'
    || (courseDetailStatus === 'ready' && distanceYards == null && locationStatus === 'active');
  const showManualDistance = gpsFailed || greenUnavailable;

  return (
    <section className="round-conditions" aria-label="Current round conditions">
      <header className="round-course-title">
        <h1>{course.name}</h1>
        <p>{[course.city, course.state].filter(Boolean).join(', ')}</p>
      </header>

      <div className={`round-distance${isRefreshing || locationStatus === 'requesting' ? ' round-distance--locating' : ''}`}>
        <div className="round-panel-label">
          <LocateFixed className="ui-icon" strokeWidth={1.5} aria-hidden="true" />
          <span>Distance to the hole</span>
          <button type="button" onClick={onRefresh} disabled={isRefreshing} aria-label="Refresh GPS distance">
            <RefreshCw className={`ui-icon${isRefreshing ? ' is-spinning' : ''}`} strokeWidth={1.5} aria-hidden="true" />
          </button>
        </div>
        {showManualDistance ? (
          <div className="round-distance__manual">
            <label htmlFor="distance">Enter distance manually</label>
            <div className="p-inputgroup">
              <InputText
                id="distance"
                inputMode="numeric"
                value={manualDistance}
                onChange={(event) => onManualDistanceChange(event.target.value.replace(/[^0-9.]/g, ''))}
                placeholder={measurementSystem === 'metric' ? 'e.g. 137' : 'e.g. 150'}
              />
              <span className="p-inputgroup-addon">{distanceUnitLabel(measurementSystem)}</span>
            </div>
            <small>{gpsFailed ? locationMessage(locationStatus, currentHole) : `No green GPS for hole ${currentHole}.`}</small>
          </div>
        ) : displayDistance == null ? (
          <p className="round-distance__message">{locationMessage(locationStatus, currentHole)}</p>
        ) : (
          <div className="round-distance__value" aria-live="polite">
            <strong>{displayDistance}</strong>
            <span>{distanceUnitLabel(measurementSystem)}</span>
          </div>
        )}
      </div>

      <div className="round-hole-selector">
        <div className="round-hole-selector__heading">
          <span>Hole</span>
          <strong>{currentHole} of {holeCount}</strong>
        </div>
        <div className="round-hole-selector__strip" role="group" aria-label="Select hole">
          {Array.from({ length: holeCount }, (_, index) => index + 1).map((hole) => (
            <button
              key={hole}
              type="button"
              className={hole === currentHole ? 'active' : ''}
              aria-pressed={hole === currentHole}
              onClick={() => onHoleChange(hole)}
            >
              {hole}
            </button>
          ))}
        </div>
      </div>

      <div className="round-wind">
        <Wind className="round-wind__icon" strokeWidth={1.35} aria-hidden="true" />
        <div>
          <span className="round-panel-label">Wind</span>
          <strong>{wind?.relativeToTarget?.summary ?? wind?.summary ?? 'Weather updates when GPS is ready.'}</strong>
        </div>
        {displayWind != null && (
          <div className="round-wind__speed">
            <strong>{displayWind}</strong>
            <span>{speedUnitLabel(measurementSystem)}</span>
          </div>
        )}
      </div>
    </section>
  );
};

export default RoundConditionsPanel;
